
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Settings,
  X,
  Shield,
  ShieldOff,
  Lock,
  Unlock,
  Edit3,
  Check,
  Users,
  Radio,
} from "lucide-react";

interface RoomSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isHost: boolean;
  roomTitle: string;
  hostOnly: boolean;
  isLocked: boolean;
  participantCount: number;
  roomId: string;
  onToggleHostOnly: () => void;
  onToggleLock: () => void;
  onRenameRoom: (title: string) => void;
}

export default function RoomSettingsModal({
  isOpen,
  onClose,
  isHost,
  roomTitle,
  hostOnly,
  isLocked,
  participantCount,
  roomId,
  onToggleHostOnly,
  onToggleLock,
  onRenameRoom,
}: RoomSettingsModalProps) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(roomTitle);

  useEffect(() => {
    setTitleDraft(roomTitle);
  }, [roomTitle]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  function commitTitle() {
    setEditingTitle(false);
    const next = titleDraft.trim();
    if (next && next !== roomTitle) onRenameRoom(next);
    else setTitleDraft(roomTitle);
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-[#0a0a1f]/45 backdrop-blur-xl flex items-center justify-center p-4 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
        >
          <motion.div
            className="glass-strong rounded-xl w-full max-w-md p-6 relative overflow-hidden border-white/15"
            initial={{ scale: 0.96, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 12 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.07] via-transparent to-transparent pointer-events-none" />
            <div className="flex items-center justify-between mb-5 relative">
              <div className="flex items-center gap-2">
 <span className="w-8 h-8 flex items-center justify-center"><Settings className="w-4 h-4 text-primary" /></span>
                <h3 className="text-sm font-semibold">Room Settings</h3>
              </div>
              <button
                onClick={onClose}
 className="w-8 h-8 flex items-center justify-center transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Room identity */}
            <div className="space-y-3 mb-5 relative">
              <div>
                <label className="block text-[11px] font-bold tracking-widest uppercase text-muted-foreground mb-1.5">
                  Room title
                </label>
                {isHost && editingTitle ? (
                  <div className="flex gap-2">
                    <input
                      autoFocus
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitTitle();
                        if (e.key === "Escape") {
                          setEditingTitle(false);
                          setTitleDraft(roomTitle);
                        }
                      }}
                      maxLength={80}
                      className="flex-1 glass-input rounded-xl px-3 py-2.5 text-xs outline-none"
                    />
                    <button
                      onClick={commitTitle}
                      className="px-4 py-2 glass-button-primary text-xs font-semibold rounded-xl flex items-center gap-1.5"
                    >
                      <Check className="w-3 h-3" /> Save
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 glass rounded-xl px-4 py-3">
                    <span className="text-xs text-foreground truncate">{roomTitle}</span>
                    {isHost && (
                      <button
                        onClick={() => {
                          setTitleDraft(roomTitle);
                          setEditingTitle(true);
                        }}
                        className="shrink-0 text-muted-foreground hover:text-primary"
                        title="Rename room"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-bold tracking-widest uppercase text-muted-foreground mb-1.5">
                  Room code
                </label>
                <div className="glass-strong rounded-xl px-4 py-3 text-xs font-mono font-bold tracking-widest text-primary">
                  {roomId}
                </div>
              </div>
            </div>

            {/* Host-only controls toggle */}
            {isHost && (
              <div className="space-y-3 mb-5 relative">
                <label className="flex items-center justify-between gap-3 p-4 glass rounded-xl cursor-pointer hover:glass-strong transition-all">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {hostOnly ? (
                      <Shield className="w-4 h-4 text-primary shrink-0" />
                    ) : (
                      <ShieldOff className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-foreground">Host-only controls</div>
                      <div className="text-[10px] text-muted-foreground">
                        Only the host can control playback
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={onToggleHostOnly}
                    className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
                      hostOnly ? "bg-primary" : "bg-secondary"
                    }`}
                    role="switch"
                    aria-checked={hostOnly}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                        hostOnly ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </button>
                </label>

                <label className="flex items-center justify-between gap-3 p-4 glass rounded-xl cursor-pointer hover:glass-strong transition-all">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {isLocked ? (
                      <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                    ) : (
                      <Unlock className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-foreground">Lock room</div>
                      <div className="text-[10px] text-muted-foreground">
                        Prevent new viewers from joining
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={onToggleLock}
                    className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
                      isLocked ? "bg-muted-foreground" : "bg-secondary"
                    }`}
                    role="switch"
                    aria-checked={isLocked}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                        isLocked ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </button>
                </label>
              </div>
            )}

            {/* Room stats */}
            <div className="grid grid-cols-2 gap-3 mb-2 relative">
              <div className="glass rounded-xl p-4 text-center relative overflow-hidden">
                <Users className="w-3.5 h-3.5 text-primary mx-auto mb-1" />
                <div className="text-lg font-semibold text-foreground tabular-nums">
                  {participantCount}
                </div>
                <div className="text-[10px] text-muted-foreground">viewers</div>
              </div>
              <div className="glass rounded-xl p-4 text-center relative overflow-hidden">
                <Radio
                  className={`w-3.5 h-3.5 mx-auto mb-1 ${
                    hostOnly ? "text-primary" : "text-muted-foreground"
                  }`}
                />
                <div className="text-lg font-semibold text-foreground">
                  {hostOnly ? "Host" : "Open"}
                </div>
                <div className="text-[10px] text-muted-foreground">controls</div>
              </div>
            </div>

            {!isHost && (
              <p className="text-[10px] text-muted-foreground text-center mt-3">
                Only the host can change room settings.
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
